import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogTitle,
  DialogActions,
  Button,
  Tooltip,
  Input,
  FieldError,
  Field,
  DialogDescription,
  Typography,
} from '@eye-able/ui';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import fetchWrapper from '@http/fetchWrapper';
import { api } from '@http/constants';
import { Pencil } from 'lucide-react';
import { useNotification } from '@eye-able/ui';
import { T } from '@/i18n/T';
import { useReloadLicenseData } from '@http/queries';
import LoadingButton from '@components/Button/LoadingButton';

interface EditDomainAliasDialogProps {
  auditEntryId: number;
  domain: string;
  domainAlias: string;
}

const DOMAIN_ALIAS_MAX_LENGTH = 60;

export const EditDomainAliasDialog = ({
  auditEntryId,
  domain,
  domainAlias,
}: EditDomainAliasDialogProps) => {
  const { t } = useInlineTranslation();
  const notification = useNotification();
  const reloadLicenseData = useReloadLicenseData();

  const [alias, setAlias] = useState<string | null>(domainAlias);
  const [open, setOpen] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: { domainAlias: string | null; auditEntryId: number }) =>
      fetchWrapper.post(api.auditEntriesDomainAlias, body),
    onSuccess: async () => {
      await reloadLicenseData();
    },
    onError: (error) => {
      notification.error(error?.message || error);
    },
  });

  const saveAction = async (newAlias: string | null = alias) => {
    if ((newAlias?.length ?? 0) > DOMAIN_ALIAS_MAX_LENGTH) {
      setValidationError(t('domainAliasMaxLengthError'));
      return false;
    } else {
      const alias = newAlias?.trim() || null;
      await mutation.mutateAsync({ domainAlias: alias, auditEntryId });
      setOpen(false);
    }
    return false;
  };

  const handleInputChange = (nextAlias: string) => {
    setAlias(nextAlias);
    setValidationError(
      nextAlias.length > DOMAIN_ALIAS_MAX_LENGTH
        ? t('domainAliasMaxLengthError')
        : null,
    );
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setAlias(domainAlias);
      setValidationError(null);
    }
    setOpen(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Tooltip content={t('editDomainAlias')}>
        <Button
          className='eye:pointer-events-auto'
          type='button'
          variant='ghost'
          size='icon-xs'
          aria-label={t('editDomainAlias')}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenChange(true);
          }}
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <Pencil aria-hidden />
        </Button>
      </Tooltip>

      <DialogContent maxWidth={'sm'} className='eye:w-full'>
        <DialogTitle>
          <T k={'editDomainAlias'} />
        </DialogTitle>
        <DialogDescription>
          <div className='eye:mt-2xs eye:mb-s eye:flex eye:flex-col eye:gap-3xs'>
            <T k={'editDomainAliasDescription'} />
            <Field data-invalid={Boolean(validationError) || undefined}>
              <Input
                aria-invalid={Boolean(validationError)}
                id={'domainAlias'}
                label={t('domainAliasName')}
                type='text'
                minLength={1}
                value={alias || ''}
                onChange={(e) => handleInputChange(e.target.value)}
              />
              {validationError && <FieldError>{validationError}</FieldError>}
            </Field>
            <div className='eye:bg-grey-10 eye:p-xs eye:break-all eye:rounded-md eye:flex eye:gap-5xs eye:items-start'>
              <Typography variant='body-s-highlight' className='eye:shrink-0'>
                URL:
              </Typography>
              <Typography variant='body-s'>{domain}</Typography>
            </div>
          </div>
        </DialogDescription>
        <DialogActions>
          <DialogClose asChild>
            <Button variant='outline'>{t('cancel')}</Button>
          </DialogClose>
          <LoadingButton
            type='button'
            disabled={mutation.isPending || Boolean(validationError)}
            variant='yellow'
            onClick={() => {
              void saveAction(alias);
            }}
            loading={mutation.isPending}
          >
            {t('save')}
          </LoadingButton>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};
